import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchCalendarData,
  type CalendarData,
  type CalendarDay,
} from "../lib/api";
import { adToBS, bsToAD, getCurrentBSDate } from "../lib/dateConverter";
import { calendarDB } from "../lib/indexedDB";

const getKey = (bsYear: number, bsMonth: number) => `bs${bsYear}-${bsMonth}`;

// Helper functions for month toggle logic
const getBSMonthStartADMonth = (bsYear: number, bsMonth: number): { adYear: number; adMonth: number } | null => {
  const adDate = bsToAD(bsYear, bsMonth, 1);
  if (!adDate) return null;
  return { adYear: adDate.ad_y, adMonth: adDate.ad_m };
};

const getADMonthStartBSMonth = (adYear: number, adMonth: number): { bsYear: number; bsMonth: number } | null => {
  const bsDate = adToBS(adYear, adMonth, 1);
  if (!bsDate) return null;
  return { bsYear: bsDate.bs_y, bsMonth: bsDate.bs_m };
};

const getTodayDates = (): { bsDate: string | null; adDate: string | null } => {
  const currentBS = getCurrentBSDate();
  if (!currentBS) return { bsDate: null, adDate: null };
  
  const bsDate = `${currentBS.bs_y}-${String(currentBS.bs_m).padStart(2, "0")}-${String(currentBS.bs_d).padStart(2, "0")}`;
  const adDateObj = bsToAD(currentBS.bs_y, currentBS.bs_m, currentBS.bs_d);
  const adDate = adDateObj 
    ? `${adDateObj.ad_y}-${String(adDateObj.ad_m).padStart(2, "0")}-${String(adDateObj.ad_d).padStart(2, "0")}`
    : null;
  
  return { bsDate, adDate };
};

const isDateInBSMonth = (
  dateKey: string, // BS format: "2082-09-12"
  bsYear: number,
  bsMonth: number,
  calendarData: Record<string, CalendarData>
): boolean => {
  const key = getKey(bsYear, bsMonth);
  const data = calendarData[key];
  if (!data || !data.days) return false;
  return data.days.some(day => day.bs.date === dateKey);
};

const isDateInADMonth = (
  dateKey: string, // AD format: "2025-12-27"
  adYear: number,
  adMonth: number,
  calendarData: Record<string, CalendarData>
): boolean => {
  // Search through all BS months to find if this AD date exists
  for (const key of Object.keys(calendarData)) {
    const data = calendarData[key];
    if (data && data.days) {
      const found = data.days.find(day => day.ad?.date === dateKey);
      if (found && found.ad) {
        return found.ad.ad_y === adYear && found.ad.ad_m === adMonth;
      }
    }
  }
  return false;
};

export interface UseNepaliCalendarProps {
  year?: number; // AD year (always AD format) - used if bsYear not provided
  month?: number; // AD month (always AD format, 1-12) - used if bsMonth not provided
  bsYear?: number; // BS year (optional, takes precedence over year/month conversion if provided)
  bsMonth?: number; // BS month (optional, takes precedence over year/month conversion if provided)
  calendarMode?: "bs" | "ad";
  value?: string; // Date in format "YYYY-MM-DD" (always AD format)
  onDateSelect?: (date: CalendarDay) => void;
  onMonthChange?: (year: number, month: number) => void;
  onCalendarModeChange?: (mode: "bs" | "ad") => void;
}

export const useNepaliCalendar = ({
  year: initialYear,
  month: initialMonth,
  bsYear: initialBSYear,
  bsMonth: initialBSMonth,
  calendarMode: initialCalendarMode = "bs",
  value,
  onDateSelect,
  onMonthChange,
  onCalendarModeChange,
}: UseNepaliCalendarProps) => {
  const getInitialBSYear = () => {
    // Priority 1: bsYear prop (direct BS year) - takes highest precedence
    if (initialBSYear !== undefined && initialBSYear !== null) {
      return initialBSYear;
    }
    
    // Priority 2: Convert AD year/month to BS
    if (initialYear && initialMonth) {
      const bsDate = adToBS(initialYear, initialMonth, 15); // Use day 15 as anchor
      if (bsDate) {
        return bsDate.bs_y;
      }
    }
    
    // Priority 3: Use date converter to get current BS year
    const currentBS = getCurrentBSDate();
    return currentBS ? currentBS.bs_y : new Date().getFullYear() + 57;
  };

  // Use explicit BS year/month state - API expects BS year/month
  const [currentBSYear, setCurrentBSYear] = useState<number>(
    getInitialBSYear()
  );

  const getInitialBSMonth = () => {
    // Priority 1: bsMonth prop (direct BS month) - takes highest precedence
    if (initialBSMonth !== undefined && initialBSMonth !== null) {
      return initialBSMonth;
    }
    
    // Priority 2: Convert AD year/month to BS
    if (initialYear && initialMonth) {
      const bsDate = adToBS(initialYear, initialMonth, 15); // Use day 15 as anchor
      if (bsDate) {
        return bsDate.bs_m;
      }
    }
    
    // Priority 3: Use date converter to get current BS month
    const currentBS = getCurrentBSDate();
    return currentBS ? currentBS.bs_m : 1;
  };

  const [currentBSMonth, setCurrentBSMonth] = useState<number>(
    getInitialBSMonth()
  );

  // calendarData keyed by "bs{year}-{month}" so we can hold many years
  const [calendarData, setCalendarData] = useState<
    Record<string, CalendarData>
  >({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isYearLoading, setIsYearLoading] = useState<boolean>(false);
  const [isMonthLoading, setIsMonthLoading] = useState<boolean>(false);
  const [calendarMode, setCalendarMode] = useState<"bs" | "ad">(
    initialCalendarMode
  );
  const [isIndexedDBInitialized, setIsIndexedDBInitialized] =
    useState<boolean>(false);

  const [showMonthDropdown, setShowMonthDropdown] = useState<boolean>(false);
  const [showYearDropdown, setShowYearDropdown] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<string>("");
  // Track selection in both calendars so it persists across mode toggles
  const [selectedBSKey, setSelectedBSKey] = useState<string>("");
  const [selectedADKey, setSelectedADKey] = useState<string>("");
  // Track primary AD month/year for AD mode navigation
  const [primaryADMonth, setPrimaryADMonth] = useState<number | null>(null);
  const [primaryADYear, setPrimaryADYear] = useState<number | null>(null);
  // Track original BS month/year when entering AD mode, to restore when toggling back
  const [originalBSMonth, setOriginalBSMonth] = useState<number | null>(null);
  const [originalBSYear, setOriginalBSYear] = useState<number | null>(null);
  // Track AD month when switching to AD mode, to detect if user navigated
  const [originalADMonth, setOriginalADMonth] = useState<number | null>(null);
  const [originalADYear, setOriginalADYear] = useState<number | null>(null);

  const titleRef = useRef<HTMLDivElement>(null);

  // initialize DB
  useEffect(() => {
    const initDB = async () => {
      try {
        await calendarDB.init();
        setIsIndexedDBInitialized(true);
      } catch (error) {
        console.error("Failed to init IndexedDB:", error);
        setIsIndexedDBInitialized(true);
      }
    };
    initDB();
  }, []);

  const fetchMonthData = useCallback(
    async (bsYear: number, bsMonth: number): Promise<CalendarData | null> => {
      try {
        const key = getKey(bsYear, bsMonth);

        // check cache in state first
        if (calendarData[key]) {
          return calendarData[key];
        }

        // try IndexedDB
        if (isIndexedDBInitialized) {
          const cached = await calendarDB.get(bsYear, bsMonth);
          if (cached) {
            setCalendarData((prev) => ({ ...prev, [key]: cached }));
            return cached;
          }
        }

        // fetch from API (API expects BS year/month)
        const data = await fetchCalendarData(bsYear, bsMonth);
        if (data) {
          setCalendarData((prev) => ({ ...prev, [key]: data }));
          if (isIndexedDBInitialized) {
            await calendarDB.set(bsYear, bsMonth, data);
          }
        }
        return data;
      } catch (error) {
        console.error("Error fetching month data:", error);
        return null;
      }
    },
    [isIndexedDBInitialized, calendarData]
  );

  const fetchYearData = useCallback(
    async (bsYear: number) => {
      setIsYearLoading(true);
      try {
        const yearData: Record<string, CalendarData> = {};
        for (let month = 1; month <= 12; month++) {
          const data = await fetchMonthData(bsYear, month);
          if (data) {
            yearData[getKey(bsYear, month)] = data;
          }
        }
        setCalendarData((prev) => ({ ...prev, ...yearData }));
      } catch (err) {
        console.error("Error fetching year data:", err);
      } finally {
        setIsYearLoading(false);
      }
    },
    [fetchMonthData]
  );

  // helper: find cached or fetch BS month that contains AD month/year
  const findBSMonthForAD = useCallback(
    async (adY: number, adM: number, searchAroundBSYear = currentBSYear) => {
      // Search among cached calendarData first (fast path)
      for (const key of Object.keys(calendarData)) {
        const data = calendarData[key];
        if (!data || !data.days) continue;
        const found = data.days.find(
          (d) => d.ad && d.ad.ad_y === adY && d.ad.ad_m === adM
        );
        if (found) {
          // the key is bs{year}-{month}
          const [, rest] = key.split("bs");
          const [bsYearStr, bsMonthStr] = rest.split("-");
          return {
            bsYear: Number(bsYearStr),
            bsMonth: Number(bsMonthStr),
            data,
          };
        }
      }

      // Not found in cache — search by fetching neighboring BS months
      // Start from currentBSYear/currentBSMonth and move forward/backward up to e.g. 24 months
      const maxSteps = 24;
      for (let step = 0; step <= maxSteps; step++) {
        // check forward
        let forwardMonthIndex = currentBSMonth - 1 + step;
        let fYear = currentBSYear + Math.floor(forwardMonthIndex / 12);
        let fMonth = (forwardMonthIndex % 12) + 1;
        const forwardData = await fetchMonthData(fYear, fMonth);
        if (forwardData) {
          const found = forwardData.days.find(
            (d) => d.ad && d.ad.ad_y === adY && d.ad.ad_m === adM
          );
          if (found)
            return { bsYear: fYear, bsMonth: fMonth, data: forwardData };
        }

        if (step === 0) continue;

        // check backward
        let backwardMonthIndex = currentBSMonth - 1 - step;
        let bYear = currentBSYear + Math.floor(backwardMonthIndex / 12);
        while (backwardMonthIndex < 0) {
          backwardMonthIndex += 12;
          bYear -= 1;
        }
        let bMonth = (backwardMonthIndex % 12) + 1;
        const backwardData = await fetchMonthData(bYear, bMonth);
        if (backwardData) {
          const found = backwardData.days.find(
            (d) => d.ad && d.ad.ad_y === adY && d.ad.ad_m === adM
          );
          if (found)
            return { bsYear: bYear, bsMonth: bMonth, data: backwardData };
        }
      }

      // fallback null
      return null;
    },
    [calendarData, currentBSMonth, currentBSYear, fetchMonthData]
  );

  // Fetch all BS months that contain days from a target AD month
  const fetchAllBSMonthsForADMonth = useCallback(
    async (adYear: number, adMonth: number): Promise<CalendarDay[]> => {
      const allDays: CalendarDay[] = [];
      const fetchedBSMonths = new Set<string>(); // Track which BS months we've already fetched

      // Validate AD year is within supported range (1921-2032 based on component validation)
      // Note: dateConverter supports up to 2040, but component validation limits to 2032
      if (adYear < 1921 || adYear > 2032) {
        console.warn(`AD year ${adYear} is out of supported range (1921-2032)`);
        return [];
      }

      // Strategy: Find BS months that contain days 1, 15, and last day of the AD month
      // This should cover all BS months that contain any part of the AD month
      const targetDays = [1, 15];
      // Get last day of the AD month
      const lastDayOfMonth = new Date(adYear, adMonth, 0).getDate();
      targetDays.push(lastDayOfMonth);

      for (const adDay of targetDays) {
        try {
          const bsDate = adToBS(adYear, adMonth, adDay);
          if (bsDate) {
            const bsMonthKey = `bs${bsDate.bs_y}-${bsDate.bs_m}`;
            if (!fetchedBSMonths.has(bsMonthKey)) {
              fetchedBSMonths.add(bsMonthKey);
              // Check IndexedDB first, then fetch if needed
              const data = await fetchMonthData(bsDate.bs_y, bsDate.bs_m);
              if (data && data.days) {
                // Filter days that belong to the target AD month
                const daysInTargetMonth = data.days.filter(
                  (day) =>
                    day.ad && day.ad.ad_y === adYear && day.ad.ad_m === adMonth
                );
                allDays.push(...daysInTargetMonth);
              }
            }
          }
        } catch (error) {
          // Skip dates that are out of range or fail conversion
          console.warn(
            `Failed to convert AD date ${adYear}-${adMonth}-${adDay} to BS:`,
            error
          );
          continue;
        }
      }

      // Remove duplicates and sort by AD day
      const uniqueDays = new Map<string, CalendarDay>();
      for (const day of allDays) {
        if (day.ad && day.ad.date) {
          uniqueDays.set(day.ad.date, day);
        }
      }

      return Array.from(uniqueDays.values()).sort((a, b) => {
        if (!a.ad || !b.ad) return 0;
        return a.ad.ad_d - b.ad.ad_d;
      });
    },
    [fetchMonthData]
  );

  // initial fetch effect
  useEffect(() => {
    const fetchInitial = async () => {
      if (!isIndexedDBInitialized) return;

      setIsLoading(true);

      // If no initialYear/month provided, detect and load current month
      if (!initialYear && !initialMonth) {
        // Use date converter to get current BS date
        const currentBS = getCurrentBSDate();
        console.log("currentBS", currentBS);
        if (currentBS) {
          const approxBSYear = currentBS.bs_y;
          const approxBSMonth = currentBS.bs_m;

          // Use the converted BS month/year directly or search nearby
          let foundMonth: number | null = null;
          let foundData: CalendarData | null = null;

          // Try the calculated month first
          try {
            const data = await fetchMonthData(approxBSYear, approxBSMonth);
            if (data && data.days) {
              const hasToday = data.days.some((d) => d.today);
              if (hasToday) {
                foundMonth = approxBSMonth;
                foundData = data;
                setCurrentBSYear(approxBSYear);
                setCurrentBSMonth(approxBSMonth);
              }
            }
          } catch (err) {
            console.error(err);
          }

          // If not found, search nearby months
          if (!foundMonth) {
            const searchMonths = [
              approxBSMonth,
              approxBSMonth - 1,
              approxBSMonth + 1,
            ];
            for (const m of searchMonths) {
              if (m < 1 || m > 12) continue;
              try {
                const data = await fetchMonthData(approxBSYear, m);
                if (data && data.days) {
                  const hasToday = data.days.some((d) => d.today);
                  if (hasToday) {
                    foundMonth = m;
                    foundData = data;
                    setCurrentBSYear(approxBSYear);
                    setCurrentBSMonth(m);
                    break;
                  }
                }
              } catch (err) {
                console.error(err);
              }
            }
          }

          // If still no month found, use the calculated month as fallback
          if (!foundMonth) {
            foundMonth = approxBSMonth;
            setCurrentBSYear(approxBSYear);
            setCurrentBSMonth(approxBSMonth);
            foundData = await fetchMonthData(approxBSYear, approxBSMonth);
          }

          // Prefetch neighbors
          fetchMonthData(approxBSYear, Math.max(1, foundMonth - 1));
          fetchMonthData(approxBSYear, Math.min(12, foundMonth + 1));

          // If initial calendar mode is AD, set primary AD month
          if (initialCalendarMode === "ad" && foundData) {
            const currentData = foundData;
            if (
              currentData &&
              currentData.days &&
              currentData.days.length > 0
            ) {
              // Priority 1: Find today's date in the current BS month data
              const todayDay = currentData.days.find(
                (day) => day.today === true
              );
              if (todayDay && todayDay.ad) {
                setPrimaryADMonth(todayDay.ad.ad_m);
                setPrimaryADYear(todayDay.ad.ad_y);
              } else {
                // Priority 2: Use majority month as fallback
                const monthCounts: Record<string, number> = {};
                for (const day of currentData.days) {
                  if (day.ad && day.ad.date) {
                    const dateParts = day.ad.date.split("-");
                    if (dateParts.length === 3) {
                      const key = `${dateParts[0]}-${dateParts[1]}`;
                      monthCounts[key] = (monthCounts[key] || 0) + 1;
                    }
                  }
                }
                let maxCount = 0;
                let majorityMonth = "";
                for (const [key, count] of Object.entries(monthCounts)) {
                  if (count > maxCount) {
                    maxCount = count;
                    majorityMonth = key;
                  }
                }

                if (majorityMonth) {
                  const [y, m] = majorityMonth.split("-").map(Number);
                  setPrimaryADMonth(m);
                  setPrimaryADYear(y);
                }
              }
            }
          }

          setIsLoading(false);
          return;
        }
      }

      // fetch the current BS month if initialYear/initialMonth were provided
      const data = await fetchMonthData(currentBSYear, currentBSMonth);
      if (!data) {
        // fallback: fetch whole year
        await fetchYearData(currentBSYear);
      }

      // prefetch adjacent months
      const prevIndex = currentBSMonth - 1;
      const nextIndex = currentBSMonth + 1;
      if (prevIndex >= 1) fetchMonthData(currentBSYear, prevIndex);
      if (nextIndex <= 12) fetchMonthData(currentBSYear, nextIndex);

      // If initialYear/initialMonth are provided, set primary AD month/year for AD mode
      if (initialYear && initialMonth) {
        if (initialCalendarMode === "ad") {
          // Use the provided AD year/month directly
          setPrimaryADMonth(initialMonth);
          setPrimaryADYear(initialYear);
        } else {
          // For BS mode, find the majority AD month from the fetched BS month data
          if (data && data.days && data.days.length > 0) {
            const monthCounts: Record<string, number> = {};
            for (const day of data.days) {
              if (day.ad && day.ad.date) {
                const dateParts = day.ad.date.split("-");
                if (dateParts.length === 3) {
                  const key = `${dateParts[0]}-${dateParts[1]}`;
                  monthCounts[key] = (monthCounts[key] || 0) + 1;
                }
              }
            }
            let maxCount = 0;
            let majorityMonth = "";
            for (const [key, count] of Object.entries(monthCounts)) {
              if (count > maxCount) {
                maxCount = count;
                majorityMonth = key;
              }
            }

            if (majorityMonth) {
              const [y, m] = majorityMonth.split("-").map(Number);
              setPrimaryADMonth(m);
              setPrimaryADYear(y);
            }
          }
        }
      } else if (initialCalendarMode === "ad" && data) {
        // If no initialYear/month provided but AD mode, use majority month from data
        const currentData = data;
        if (currentData && currentData.days && currentData.days.length > 0) {
          // Priority 1: Find today's date in the current BS month data
          const todayDay = currentData.days.find((day) => day.today === true);
          if (todayDay && todayDay.ad) {
            setPrimaryADMonth(todayDay.ad.ad_m);
            setPrimaryADYear(todayDay.ad.ad_y);
          } else {
            // Priority 2: Use majority month as fallback
            const monthCounts: Record<string, number> = {};
            for (const day of currentData.days) {
              if (day.ad && day.ad.date) {
                const dateParts = day.ad.date.split("-");
                if (dateParts.length === 3) {
                  const key = `${dateParts[0]}-${dateParts[1]}`;
                  monthCounts[key] = (monthCounts[key] || 0) + 1;
                }
              }
            }
            let maxCount = 0;
            let majorityMonth = "";
            for (const [key, count] of Object.entries(monthCounts)) {
              if (count > maxCount) {
                maxCount = count;
                majorityMonth = key;
              }
            }

            if (majorityMonth) {
              const [y, m] = majorityMonth.split("-").map(Number);
              setPrimaryADMonth(m);
              setPrimaryADYear(y);
            }
          }
        }
      }

      setIsLoading(false);
    };
    fetchInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIndexedDBInitialized]);

  const prevValueRef = useRef<string | undefined>(undefined);

  // Handle value prop: parse date and set selected date
  // IMPORTANT: value prop is ALWAYS in AD format, regardless of calendarMode
  useEffect(() => {
    if (!value) {
      prevValueRef.current = value;
      return;
    }

    // Wait for IndexedDB to initialize before navigating/fetching,
    // but we can still set the selected keys immediately
    if (!isIndexedDBInitialized) {
      // Set keys immediately even if DB not ready
      const dateParts = value.split("-");
      if (dateParts.length === 3) {
        const adYear = parseInt(dateParts[0], 10);
        const adMonth = parseInt(dateParts[1], 10);
        const adDay = parseInt(dateParts[2], 10);
        if (!isNaN(adYear) && !isNaN(adMonth) && !isNaN(adDay)) {
          if (adYear >= 1921 && adYear <= 2032) {
            const adDateString = `${adYear}-${String(adMonth).padStart(
              2,
              "0"
            )}-${String(adDay).padStart(2, "0")}`;
            const bsDate = adToBS(adYear, adMonth, adDay);
            if (bsDate) {
              const bsDateString = `${bsDate.bs_y}-${String(
                bsDate.bs_m
              ).padStart(2, "0")}-${String(bsDate.bs_d).padStart(2, "0")}`;
              setSelectedADKey(adDateString);
              setSelectedBSKey(bsDateString);
              // Set primary AD month/year for AD mode
              setPrimaryADMonth(adMonth);
              setPrimaryADYear(adYear);
            }
          }
        }
      }
      prevValueRef.current = value;
      return;
    }

    // Only update if value actually changed
    if (prevValueRef.current === value) {
      return;
    }

    // Value is always in AD format - parse as AD date
    const dateParts = value.split("-");
    if (dateParts.length !== 3) {
      prevValueRef.current = value;
      return;
    }

    const adYear = parseInt(dateParts[0], 10);
    const adMonth = parseInt(dateParts[1], 10);
    const adDay = parseInt(dateParts[2], 10);

    if (isNaN(adYear) || isNaN(adMonth) || isNaN(adDay)) {
      prevValueRef.current = value;
      return;
    }

    // Validate AD date range
    if (adYear < 1921 || adYear > 2032) {
      console.warn(`AD value ${value} is out of supported range (1921-2032).`);
      prevValueRef.current = value;
      return;
    }

    // Value is in AD format - set selectedADKey directly
    const adDateString = `${adYear}-${String(adMonth).padStart(
      2,
      "0"
    )}-${String(adDay).padStart(2, "0")}`;

    // Convert AD to BS to get selectedBSKey
    const bsDate = adToBS(adYear, adMonth, adDay);
    if (!bsDate) {
      console.warn(`Failed to convert AD date ${value} to BS.`);
      prevValueRef.current = value;
      return;
    }

    const bsDateString = `${bsDate.bs_y}-${String(bsDate.bs_m).padStart(
      2,
      "0"
    )}-${String(bsDate.bs_d).padStart(2, "0")}`;

    // Always set the keys when value changes (they're derived from value)
    setSelectedADKey(adDateString);
    setSelectedBSKey(bsDateString);

    // Set primary AD month/year for AD mode
    setPrimaryADMonth(adMonth);
    setPrimaryADYear(adYear);

    // Navigate based on calendarMode:
    // - If calendarMode="ad": navigate to AD month (set primaryADMonth/primaryADYear, fetch BS months)
    // - If calendarMode="bs": navigate to BS month
    const currentData = getDataForKey(currentBSYear, currentBSMonth);
    const needsNavigation =
      !currentData ||
      (calendarMode === "bs"
        ? !currentData.days.find((d) => d.bs?.date === bsDateString)
        : !currentData.days.find((d) => d.ad?.date === adDateString));

    if (needsNavigation) {
      if (calendarMode === "ad") {
        // AD mode: navigate to AD month (fetch BS months that contain this AD month)
        // We already set primaryADMonth/primaryADYear above
        // Fetch the BS month that contains this AD date
        setCurrentBSMonth(bsDate.bs_m);
        setCurrentBSYear(bsDate.bs_y);
        fetchMonthData(bsDate.bs_y, bsDate.bs_m);
        onMonthChange?.(adYear, adMonth);
      } else {
        // BS mode: navigate to BS month
        setCurrentBSMonth(bsDate.bs_m);
        setCurrentBSYear(bsDate.bs_y);
        fetchMonthData(bsDate.bs_y, bsDate.bs_m);
        onMonthChange?.(bsDate.bs_y, bsDate.bs_m);
      }
    }

    // Update ref after processing
    prevValueRef.current = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    value,
    calendarMode,
    isIndexedDBInitialized,
    // Note: We intentionally don't include currentBSYear/currentBSMonth in deps
    // because we only want to run this effect when value changes, not when user navigates
    // getDataForKey is stable and uses currentBSYear/currentBSMonth internally
  ]);

  const prevYearRef = useRef<number | undefined>(undefined);
  const prevMonthRef = useRef<number | undefined>(undefined);
  const prevBSYearRef = useRef<number | undefined>(undefined);
  const prevBSMonthRef = useRef<number | undefined>(undefined);

  // Handle bsYear/bsMonth props: use directly without conversion (highest priority)
  // IMPORTANT: bsYear/bsMonth props take precedence over year/month props
  useEffect(() => {
    // Skip if IndexedDB not initialized
    if (!isIndexedDBInitialized) {
      return;
    }

    // Only process if bsYear and bsMonth are both provided
    if (initialBSYear === undefined || initialBSYear === null || 
        initialBSMonth === undefined || initialBSMonth === null) {
      prevBSYearRef.current = initialBSYear;
      prevBSMonthRef.current = initialBSMonth;
      return;
    }

    // Only update if props actually changed
    if (
      prevBSYearRef.current === initialBSYear &&
      prevBSMonthRef.current === initialBSMonth
    ) {
      return;
    }

    // Validate BS month range
    if (initialBSMonth < 1 || initialBSMonth > 12) {
      console.warn(`BS month ${initialBSMonth} is out of range (1-12).`);
      prevBSYearRef.current = initialBSYear;
      prevBSMonthRef.current = initialBSMonth;
      return;
    }

    // Validate BS year range (rough check - BS years are typically 1978-2099)
    if (initialBSYear < 1978 || initialBSYear > 2099) {
      console.warn(`BS year ${initialBSYear} is out of supported range (1978-2099).`);
      prevBSYearRef.current = initialBSYear;
      prevBSMonthRef.current = initialBSMonth;
      return;
    }

    // Use BS year/month directly - no conversion needed
    setCurrentBSYear(initialBSYear);
    setCurrentBSMonth(initialBSMonth);

    // Clear primary AD month/year since we're using BS directly
    setPrimaryADMonth(null);
    setPrimaryADYear(null);

    // Fetch data for the BS month
    fetchMonthData(initialBSYear, initialBSMonth);

    // Update refs
    prevBSYearRef.current = initialBSYear;
    prevBSMonthRef.current = initialBSMonth;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialBSYear,
    initialBSMonth,
    isIndexedDBInitialized,
    // fetchMonthData is stable, so we don't need it in deps
  ]);

  // Handle year/month props: convert AD year/month to BS and navigate
  // IMPORTANT: year/month props are ALWAYS in AD format, regardless of calendarMode
  // Only process if bsYear/bsMonth are NOT provided (they take precedence)
  useEffect(() => {
    // Skip if bsYear/bsMonth are provided (they take precedence)
    if (initialBSYear !== undefined && initialBSYear !== null &&
        initialBSMonth !== undefined && initialBSMonth !== null) {
      return;
    }

    // Skip if IndexedDB not initialized or props not provided
    if (!isIndexedDBInitialized || !initialYear || !initialMonth) {
      prevYearRef.current = initialYear;
      prevMonthRef.current = initialMonth;
      return;
    }

    // Only update if props actually changed
    if (
      prevYearRef.current === initialYear &&
      prevMonthRef.current === initialMonth
    ) {
      return;
    }

    // Validate AD month range
    if (initialMonth < 1 || initialMonth > 12) {
      console.warn(`AD month ${initialMonth} is out of range (1-12).`);
      prevYearRef.current = initialYear;
      prevMonthRef.current = initialMonth;
      return;
    }

    // Validate AD year range
    if (initialYear < 1921 || initialYear > 2032) {
      console.warn(`AD year ${initialYear} is out of supported range (1921-2032).`);
      prevYearRef.current = initialYear;
      prevMonthRef.current = initialMonth;
      return;
    }

    // Convert AD year/month to BS
    const bsDate = adToBS(initialYear, initialMonth, 15); // Use day 15 as anchor
    if (!bsDate) {
      console.warn(`Failed to convert AD date ${initialYear}-${initialMonth}-15 to BS.`);
      prevYearRef.current = initialYear;
      prevMonthRef.current = initialMonth;
      return;
    }

    // Set primary AD month/year for AD mode
    setPrimaryADMonth(initialMonth);
    setPrimaryADYear(initialYear);

    // Navigate based on calendarMode
    if (calendarMode === "ad") {
      // AD mode: navigate to AD month (fetch BS months that contain this AD month)
      setCurrentBSYear(bsDate.bs_y);
      setCurrentBSMonth(bsDate.bs_m);
      fetchMonthData(bsDate.bs_y, bsDate.bs_m);
      onMonthChange?.(initialYear, initialMonth);
    } else {
      // BS mode: navigate to BS month
      setCurrentBSYear(bsDate.bs_y);
      setCurrentBSMonth(bsDate.bs_m);
      fetchMonthData(bsDate.bs_y, bsDate.bs_m);
      onMonthChange?.(bsDate.bs_y, bsDate.bs_m);
    }

    prevYearRef.current = initialYear;
    prevMonthRef.current = initialMonth;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialYear,
    initialMonth,
    initialBSYear,
    initialBSMonth,
    calendarMode,
    isIndexedDBInitialized,
    // Note: We intentionally don't include currentBSYear/currentBSMonth in deps
    // because we only want to run this effect when props change, not when user navigates
  ]);

  // Helpers to get calendarData by current bs year/month
  const getCurrentKey = () => getKey(currentBSYear, currentBSMonth);
  const getDataForKey = (bsYear: number, bsMonth: number) =>
    calendarData[getKey(bsYear, bsMonth)];

  // Helper to get the correct month/year for dropdown highlighting
  const getDropdownDisplayValues = () => {
    if (calendarMode === "bs") {
      return {
        selectedMonth: currentBSMonth,
        selectedYear: currentBSYear,
      };
    } else {
      // In AD mode, get the AD month/year from current BS month data
      const currentData = getDataForKey(currentBSYear, currentBSMonth);
      if (currentData && currentData.days && currentData.days.length > 0) {
        // Find the first day with AD data
        const dayWithAd = currentData.days.find((d) => d.ad && d.ad.date);
        if (dayWithAd) {
          return {
            selectedMonth: dayWithAd.ad.ad_m,
            selectedYear: dayWithAd.ad.ad_y,
          };
        }
      }
      // Fallback to BS values if no AD data available
      return {
        selectedMonth: currentBSMonth,
        selectedYear: currentBSYear,
      };
    }
  };

  // Helper to get AD month range for the current BS month
  const getADMonthRange = (): string | null => {
    if (calendarMode === "ad") return null;

    const currentData = getDataForKey(currentBSYear, currentBSMonth);
    if (!currentData || !currentData.days || currentData.days.length === 0) {
      return null;
    }

    let firstAdMonth = -1;
    let firstAdYear = -1;
    let lastAdMonth = -1;
    let lastAdYear = -1;

    for (const day of currentData.days) {
      if (day.ad && day.ad.date) {
        const dateParts = day.ad.date.split("-");
        if (dateParts.length === 3) {
          const month = parseInt(dateParts[1], 10);
          const year = parseInt(dateParts[0], 10);

          if (firstAdMonth === -1) {
            firstAdMonth = month;
            firstAdYear = year;
          }
          lastAdMonth = month;
          lastAdYear = year;
        }
      }
    }

    if (firstAdMonth === -1) return null;

    if (firstAdMonth === lastAdMonth && firstAdYear === lastAdYear) {
      return `(${monthsEn[firstAdMonth - 1]}, ${firstAdYear})`;
    } else {
      // Show both years if they differ, otherwise show single year
      const yearDisplay =
        firstAdYear === lastAdYear
          ? `${firstAdYear}`
          : `${firstAdYear}-${lastAdYear}`;
      return `(${monthsEn[firstAdMonth - 1]}/${
        monthsEn[lastAdMonth - 1]
      }, ${yearDisplay})`;
    }
  };

  // get displayed month/year depending on mode
  const getDisplayMonthYear = () => {
    const currentData = getDataForKey(currentBSYear, currentBSMonth);
    if (!currentData || !currentData.days || currentData.days.length === 0) {
      if (calendarMode === "bs") {
        // fallback for BS mode
        return {
          month: months[currentBSMonth - 1],
          year: toNepaliNumber(currentBSYear),
          monthNum: currentBSMonth,
          yearNum: currentBSYear,
        };
      } else {
        // In AD mode, if no data and primary AD month not set, convert BS to AD or use current date
        if (primaryADMonth !== null && primaryADYear !== null) {
          return {
            month: monthsEn[primaryADMonth - 1],
            year: primaryADYear.toString(),
            monthNum: primaryADMonth,
            yearNum: primaryADYear,
          };
        }

        // Fallback: convert current BS date to AD, or use today's date
        try {
          const bsDate = {
            bs_y: currentBSYear,
            bs_m: currentBSMonth,
            bs_d: 15,
          };
          const adDate = bsToAD(bsDate.bs_y, bsDate.bs_m, bsDate.bs_d);
          if (adDate) {
            return {
              month: monthsEn[adDate.ad_m - 1],
              year: adDate.ad_y.toString(),
              monthNum: adDate.ad_m,
              yearNum: adDate.ad_y,
            };
          }
        } catch (error) {
          // If conversion fails, use current AD date
          const today = new Date();
          return {
            month: monthsEn[today.getMonth()],
            year: today.getFullYear().toString(),
            monthNum: today.getMonth() + 1,
            yearNum: today.getFullYear(),
          };
        }

        // Final fallback: use current AD date
        const today = new Date();
        return {
          month: monthsEn[today.getMonth()],
          year: today.getFullYear().toString(),
          monthNum: today.getMonth() + 1,
          yearNum: today.getFullYear(),
        };
      }
    }

    if (calendarMode === "bs") {
      return {
        month: months[currentBSMonth - 1],
        year: toNepaliNumber(currentBSYear),
        monthNum: currentBSMonth,
        yearNum: currentBSYear,
      };
    } else {
      // AD mode: prioritize primary AD month, then selected date's AD month, then majority month
      // First, use primary AD month if set (this is the month we're navigating/viewing)
      if (primaryADMonth !== null && primaryADYear !== null) {
        // Check if this AD month exists in current BS month (if data is loaded)
        const dayInMonth = currentData.days?.find(
          (d) =>
            d.ad && d.ad.ad_y === primaryADYear && d.ad.ad_m === primaryADMonth
        );
        // Use primary AD month regardless - it's the source of truth for what we're viewing
        return {
          month: monthsEn[primaryADMonth - 1],
          year: primaryADYear.toString(),
          monthNum: primaryADMonth,
          yearNum: primaryADYear,
        };
      }

      // Second, try to use the selected date's AD month
      if (selectedADKey) {
        const dateParts = selectedADKey.split("-");
        if (dateParts.length === 3) {
          const selYear = parseInt(dateParts[0], 10);
          const selMonth = parseInt(dateParts[1], 10);
          // Check if selected date is in current BS month
          const dayInCurrentMonth = currentData.days.find(
            (d) => d.ad?.date === selectedADKey
          );
          if (dayInCurrentMonth) {
            return {
              month: monthsEn[selMonth - 1],
              year: selYear.toString(),
              monthNum: selMonth,
              yearNum: selYear,
            };
          }
        }
      }

      // Third, find the AD month with the most days in this BS month
      const monthCounts: Record<string, number> = {};
      for (const day of currentData.days) {
        if (day.ad && day.ad.date) {
          const dateParts = day.ad.date.split("-");
          if (dateParts.length === 3) {
            const key = `${dateParts[0]}-${dateParts[1]}`;
            monthCounts[key] = (monthCounts[key] || 0) + 1;
          }
        }
      }

      // Find the month with the most days
      let maxCount = 0;
      let majorityMonth = "";
      for (const [key, count] of Object.entries(monthCounts)) {
        if (count > maxCount) {
          maxCount = count;
          majorityMonth = key;
        }
      }

      let adYear = currentBSYear;
      let adMonth = currentBSMonth;

      if (majorityMonth) {
        const [y, m] = majorityMonth.split("-").map(Number);
        adYear = y;
        adMonth = m;
      } else {
        // Fallback: use first day's AD month
        for (const day of currentData.days) {
          if (day.ad && day.ad.date) {
            const dateParts = day.ad.date.split("-");
            if (dateParts.length === 3) {
              adYear = parseInt(dateParts[0], 10);
              adMonth = parseInt(dateParts[1], 10);
              break;
            }
          }
        }
      }

      return {
        month: monthsEn[adMonth - 1],
        year: adYear.toString(),
        monthNum: adMonth,
        yearNum: adYear,
      };
    }
  };

  // handle clicking a day
  const handleDayClick = (day: CalendarDay) => {
    // Persist selection for both calendars
    setSelectedBSKey(day.bs.date);
    setSelectedADKey(day.ad.date);

    // Also track the generic selectedDate for backward compatibility
    const dateStr = calendarMode === "bs" ? day.bs.date : day.ad.date;
    setSelectedDate(dateStr);

    // Set primary AD month when selecting a date in AD mode
    if (calendarMode === "ad" && day.ad) {
      setPrimaryADMonth(day.ad.ad_m);
      setPrimaryADYear(day.ad.ad_y);
    }

    onDateSelect?.(day);
  };

  // handle prev/next depending on active mode
  const handlePrevMonth = async () => {
    setIsMonthLoading(true);
    try {
      if (calendarMode === "bs") {
        // move bs month backward
        if (currentBSMonth === 1) {
          const newYear = currentBSYear - 1;
          setCurrentBSYear(newYear);
          // ensure month data exists
          if (!getDataForKey(newYear, 12)) {
            await fetchMonthData(newYear, 12);
          }
          setCurrentBSMonth(12);
          onMonthChange?.(newYear, 12);
        } else {
          const newMonth = currentBSMonth - 1;
          if (!getDataForKey(currentBSYear, newMonth)) {
            await fetchMonthData(currentBSYear, newMonth);
          }
          setCurrentBSMonth(newMonth);
          onMonthChange?.(currentBSYear, newMonth);
        }
      } else {
        // AD mode: compute target AD month/year
        const display = getDisplayMonthYear();
        const targetAdYear = display.yearNum as number;
        let targetAdMonth = (display.monthNum as number) - 1;
        let newAdYear = targetAdYear;
        if (targetAdMonth === 0) {
          targetAdMonth = 12;
          newAdYear = targetAdYear - 1;
        }

        // Use findBSMonthForAD to find the BS month that contains the target AD month
        // This ensures we find the correct BS month even if multiple AD months are in the same BS month
        const found = await findBSMonthForAD(newAdYear, targetAdMonth);
        if (found) {
          setCurrentBSYear(found.bsYear);
          setCurrentBSMonth(found.bsMonth);

          // Update primary AD month for navigation
          setPrimaryADMonth(targetAdMonth);
          setPrimaryADYear(newAdYear);

          // Ensure data exists
          if (!getDataForKey(found.bsYear, found.bsMonth)) {
            await fetchMonthData(found.bsYear, found.bsMonth);
          }

          onMonthChange?.(newAdYear, targetAdMonth);
        } else {
          // Fallback: use date converter
          const bsDate = adToBS(newAdYear, targetAdMonth, 15);
          if (bsDate) {
            setCurrentBSYear(bsDate.bs_y);
            setCurrentBSMonth(bsDate.bs_m);

            // Update primary AD month for navigation
            setPrimaryADMonth(targetAdMonth);
            setPrimaryADYear(newAdYear);

            // Ensure data exists
            if (!getDataForKey(bsDate.bs_y, bsDate.bs_m)) {
              await fetchMonthData(bsDate.bs_y, bsDate.bs_m);
            }

            onMonthChange?.(newAdYear, targetAdMonth);
          } else {
            // Final fallback: try shifting current BS month backward
            if (currentBSMonth === 1) {
              const newYear = currentBSYear - 1;
              setCurrentBSYear(newYear);
              if (!getDataForKey(newYear, 12)) {
                await fetchMonthData(newYear, 12);
              }
              setCurrentBSMonth(12);
              // determine AD month for UI
              const data = getDataForKey(newYear, 12);
              if (data) {
                const dayWithAdFirst =
                  data.days.find((d) => d.ad && d.ad.ad_d === 1) ||
                  data.days[0];
                if (dayWithAdFirst)
                  onMonthChange?.(
                    dayWithAdFirst.ad.ad_y,
                    dayWithAdFirst.ad.ad_m
                  );
              }
            } else {
              const newMonth = currentBSMonth - 1;
              if (!getDataForKey(currentBSYear, newMonth)) {
                await fetchMonthData(currentBSYear, newMonth);
              }
              setCurrentBSMonth(newMonth);
              const data = getDataForKey(currentBSYear, newMonth);
              if (data) {
                const dayWithAdFirst =
                  data.days.find((d) => d.ad && d.ad.ad_d === 1) ||
                  data.days[0];
                if (dayWithAdFirst)
                  onMonthChange?.(
                    dayWithAdFirst.ad.ad_y,
                    dayWithAdFirst.ad.ad_m
                  );
              }
            }
          }
        }
      }

      // after moving, prefetch neighbors appropriately
      const displayAfter = getDisplayMonthYear();
      if (calendarMode === "bs") {
        // prefetch prev and next bs months
        const prevM =
          currentBSMonth === 1
            ? { y: currentBSYear - 1, m: 12 }
            : { y: currentBSYear, m: currentBSMonth - 1 };
        const nextM =
          currentBSMonth === 12
            ? { y: currentBSYear + 1, m: 1 }
            : { y: currentBSYear, m: currentBSMonth + 1 };
        fetchMonthData(prevM.y, prevM.m);
        fetchMonthData(nextM.y, nextM.m);
      } else {
        // in AD mode, prefetch the BS month that contains the AD next month
        const targetAdNextMonth = (displayAfter.monthNum as number) + 1;
        let adNextY = displayAfter.yearNum as number;
        let adNextM = targetAdNextMonth;
        if (targetAdNextMonth === 13) {
          adNextM = 1;
          adNextY = (displayAfter.yearNum as number) + 1;
        }
        // find and prefetch the BS month for adNext
        findBSMonthForAD(adNextY, adNextM).then((found) => {
          if (found) fetchMonthData(found.bsYear, found.bsMonth);
        });
      }
    } finally {
      setIsMonthLoading(false);
    }
  };

  const handleNextMonth = async () => {
    setIsMonthLoading(true);
    try {
      if (calendarMode === "bs") {
        // move bs month forward
        if (currentBSMonth === 12) {
          const newYear = currentBSYear + 1;
          setCurrentBSYear(newYear);
          if (!getDataForKey(newYear, 1)) {
            await fetchMonthData(newYear, 1);
          }
          setCurrentBSMonth(1);
          onMonthChange?.(newYear, 1);
        } else {
          const newMonth = currentBSMonth + 1;
          if (!getDataForKey(currentBSYear, newMonth)) {
            await fetchMonthData(currentBSYear, newMonth);
          }
          setCurrentBSMonth(newMonth);
          onMonthChange?.(currentBSYear, newMonth);
        }
      } else {
        // AD mode: compute target AD month/year
        const display = getDisplayMonthYear();

        if (
          !display ||
          display.monthNum === undefined ||
          display.yearNum === undefined
        ) {
          console.error("AD Mode - Invalid display data");
          setIsMonthLoading(false);
          return;
        }

        const targetAdYear = display.yearNum;
        let targetAdMonth = display.monthNum + 1;
        let newAdYear = targetAdYear;
        if (targetAdMonth === 13) {
          targetAdMonth = 1;
          newAdYear = targetAdYear + 1;
        }

        // Use findBSMonthForAD to find the BS month that contains the target AD month
        // This ensures we find the correct BS month even if multiple AD months are in the same BS month
        const found = await findBSMonthForAD(newAdYear, targetAdMonth);
        if (found) {
          setCurrentBSYear(found.bsYear);
          setCurrentBSMonth(found.bsMonth);

          // Update primary AD month for navigation
          setPrimaryADMonth(targetAdMonth);
          setPrimaryADYear(newAdYear);

          // Ensure data exists
          if (!getDataForKey(found.bsYear, found.bsMonth)) {
            await fetchMonthData(found.bsYear, found.bsMonth);
          }

          onMonthChange?.(newAdYear, targetAdMonth);
        } else {
          // Fallback: use date converter
          const bsDate = adToBS(newAdYear, targetAdMonth, 15);
          if (bsDate) {
            setCurrentBSYear(bsDate.bs_y);
            setCurrentBSMonth(bsDate.bs_m);

            // Update primary AD month for navigation
            setPrimaryADMonth(targetAdMonth);
            setPrimaryADYear(newAdYear);

            // Ensure data exists
            if (!getDataForKey(bsDate.bs_y, bsDate.bs_m)) {
              await fetchMonthData(bsDate.bs_y, bsDate.bs_m);
            }

            onMonthChange?.(newAdYear, targetAdMonth);
          } else {
            // Final fallback: shift BS month forward
            if (currentBSMonth === 12) {
              const newYear = currentBSYear + 1;
              setCurrentBSYear(newYear);
              if (!getDataForKey(newYear, 1)) {
                await fetchMonthData(newYear, 1);
              }
              setCurrentBSMonth(1);
              const data = getDataForKey(newYear, 1);
              if (data) {
                const dayWithAdFirst =
                  data.days.find((d) => d.ad && d.ad.ad_d === 1) ||
                  data.days[0];
                if (dayWithAdFirst)
                  onMonthChange?.(
                    dayWithAdFirst.ad.ad_y,
                    dayWithAdFirst.ad.ad_m
                  );
              }
            } else {
              const newMonth = currentBSMonth + 1;
              if (!getDataForKey(currentBSYear, newMonth)) {
                await fetchMonthData(currentBSYear, newMonth);
              }
              setCurrentBSMonth(newMonth);
              const data = getDataForKey(currentBSYear, newMonth);
              if (data) {
                const dayWithAdFirst =
                  data.days.find((d) => d.ad && d.ad.ad_d === 1) ||
                  data.days[0];
                if (dayWithAdFirst)
                  onMonthChange?.(
                    dayWithAdFirst.ad.ad_y,
                    dayWithAdFirst.ad.ad_m
                  );
              }
            }
          }
        }
      }

      // prefetch after move
      const displayAfter = getDisplayMonthYear();
      if (calendarMode === "bs") {
        const prevM =
          currentBSMonth === 1
            ? { y: currentBSYear - 1, m: 12 }
            : { y: currentBSYear, m: currentBSMonth - 1 };
        const nextM =
          currentBSMonth === 12
            ? { y: currentBSYear + 1, m: 1 }
            : { y: currentBSYear, m: currentBSMonth + 1 };
        fetchMonthData(prevM.y, prevM.m);
        fetchMonthData(nextM.y, nextM.m);
      } else {
        // prefetch bs month for next ad month
        const targetAdNextMonth = (displayAfter.monthNum as number) + 1;
        let adNextY = displayAfter.yearNum as number;
        let adNextM = targetAdNextMonth;
        if (targetAdNextMonth === 13) {
          adNextM = 1;
          adNextY += 1;
        }
        findBSMonthForAD(adNextY, adNextM).then((found) => {
          if (found) fetchMonthData(found.bsYear, found.bsMonth);
        });
      }
    } finally {
      setIsMonthLoading(false);
    }
  };

  const handleCalendarModeChange = async (mode: "bs" | "ad") => {
    console.log("handleCalendarModeChange", mode);
    if (mode === calendarMode) return;

    // If switching to AD mode
    if (mode === "ad") {
      let targetADYear: number | null = null;
      let targetADMonth: number | null = null;
      let needsNavigation = false;
      
      // Ensure current BS month data is loaded
      if (!getDataForKey(currentBSYear, currentBSMonth)) {
        await fetchMonthData(currentBSYear, currentBSMonth);
      }
      
      const currentData = getDataForKey(currentBSYear, currentBSMonth);
      const todayDates = getTodayDates();
      
      // Priority 1: Selected date exists → ALWAYS navigate to selected date's month first, then use its AD month
      if (selectedBSKey) {
        const dateParts = selectedBSKey.split("-");
        if (dateParts.length === 3) {
          const selectedBSYear = parseInt(dateParts[0], 10);
          const selectedBSMonth = parseInt(dateParts[1], 10);
          
          // Check if selected date is in current BS month
          const isSelectedInCurrentMonth = selectedBSYear === currentBSYear && selectedBSMonth === currentBSMonth;
          
          if (!isSelectedInCurrentMonth) {
            // Selected date is NOT in current month → navigate to selected date's month first
            needsNavigation = true;
            setCurrentBSYear(selectedBSYear);
            setCurrentBSMonth(selectedBSMonth);
            // Fetch data for selected date's month
            if (!getDataForKey(selectedBSYear, selectedBSMonth)) {
              await fetchMonthData(selectedBSYear, selectedBSMonth);
            }
            // Update currentData to selected date's month
            const selectedMonthData = getDataForKey(selectedBSYear, selectedBSMonth);
            if (selectedMonthData && selectedADKey) {
              // Use selected date's AD month
              const adParts = selectedADKey.split("-");
              if (adParts.length === 3) {
                targetADYear = parseInt(adParts[0], 10);
                targetADMonth = parseInt(adParts[1], 10);
                setPrimaryADMonth(targetADMonth);
                setPrimaryADYear(targetADYear);
              }
            }
          } else if (selectedADKey && currentData) {
            // Selected date IS in current month → use selected date's AD month
            const adParts = selectedADKey.split("-");
            if (adParts.length === 3) {
              targetADYear = parseInt(adParts[0], 10);
              targetADMonth = parseInt(adParts[1], 10);
              setPrimaryADMonth(targetADMonth);
              setPrimaryADYear(targetADYear);
            }
          }
        }
      }
      
      // Priority 2: Value prop exists AND is in current BS month → use value prop's AD month
      if (!targetADYear && value) {
        const valueBSDate = adToBS(value); // value is in AD format
        if (valueBSDate) {
          const isValueInCurrentMonth = valueBSDate.bs_y === currentBSYear && valueBSDate.bs_m === currentBSMonth;
          if (isValueInCurrentMonth) {
            // Value is in current BS month → use value prop's AD month
            const dateParts = value.split("-");
            if (dateParts.length === 3) {
              targetADYear = parseInt(dateParts[0], 10);
              targetADMonth = parseInt(dateParts[1], 10);
              setPrimaryADMonth(targetADMonth);
              setPrimaryADYear(targetADYear);
            }
          }
          // If value is NOT in current month, ignore it (user has navigated)
        }
      }
      
      // Priority 3: Today's date exists AND is in current BS month → use today's AD month
      if (!targetADYear && todayDates.bsDate && currentData) {
        const isTodayInMonth = isDateInBSMonth(todayDates.bsDate, currentBSYear, currentBSMonth, calendarData);
        if (isTodayInMonth && todayDates.adDate) {
          // Today is in current BS month → use today's AD month
          const dateParts = todayDates.adDate.split("-");
          if (dateParts.length === 3) {
            targetADYear = parseInt(dateParts[0], 10);
            targetADMonth = parseInt(dateParts[1], 10);
            setPrimaryADMonth(targetADMonth);
            setPrimaryADYear(targetADYear);
          }
        }
      }
      
      // Priority 4: No selected date, no value in current month, no today → use BS month's starting AD month
      if (!targetADYear) {
        const startAD = getBSMonthStartADMonth(currentBSYear, currentBSMonth);
        if (startAD) {
          targetADYear = startAD.adYear;
          targetADMonth = startAD.adMonth;
          setPrimaryADMonth(targetADMonth);
          setPrimaryADYear(targetADYear);
        }
      }
      
      // Fetch data for the determined AD month
      if (targetADYear && targetADMonth) {
        const bsDate = adToBS(targetADYear, targetADMonth, 15);
        if (bsDate) {
          if (!getDataForKey(bsDate.bs_y, bsDate.bs_m)) {
            await fetchMonthData(bsDate.bs_y, bsDate.bs_m);
          }
          // Fetch all BS months for this AD month if function exists
          if (fetchAllBSMonthsForADMonth) {
            if (
              targetADYear >= 1921 &&
              targetADYear <= 2032 &&
              targetADMonth >= 1 &&
              targetADMonth <= 12
            ) {
              await fetchAllBSMonthsForADMonth(targetADYear, targetADMonth);
            }
          }
        }
      }
    }

    // If switching back to BS mode
    if (mode === "bs") {
      let targetBSYear: number | null = null;
      let targetBSMonth: number | null = null;
      let needsNavigation = false;
      
      // Get current AD month (from primaryADMonth or getDisplayMonthYear)
      let currentAdYear = primaryADYear;
      let currentAdMonth = primaryADMonth;
      if (currentAdYear === null || currentAdMonth === null) {
        const displayInfo = getDisplayMonthYear();
        if (displayInfo && displayInfo.yearNum !== undefined && displayInfo.monthNum !== undefined) {
          currentAdYear = displayInfo.yearNum;
          currentAdMonth = displayInfo.monthNum;
        }
      }
      
      if (currentAdYear === null || currentAdMonth === null) {
        // Fallback: use current BS month
        targetBSYear = currentBSYear;
        targetBSMonth = currentBSMonth;
      } else {
        // Ensure data is loaded for current AD month (need to find which BS month contains it)
        const bsDateForAD = adToBS(currentAdYear, currentAdMonth, 15);
        if (bsDateForAD && !getDataForKey(bsDateForAD.bs_y, bsDateForAD.bs_m)) {
          await fetchMonthData(bsDateForAD.bs_y, bsDateForAD.bs_m);
        }
        
        const todayDates = getTodayDates();
        
        // Priority 1: Selected date exists → ALWAYS navigate to selected date's month first, then use its BS month
        if (selectedADKey) {
          const dateParts = selectedADKey.split("-");
          if (dateParts.length === 3) {
            const selectedADYear = parseInt(dateParts[0], 10);
            const selectedADMonth = parseInt(dateParts[1], 10);
            
            // Check if selected date is in current AD month
            const isSelectedInCurrentMonth = selectedADYear === currentAdYear && selectedADMonth === currentAdMonth;
            
            if (!isSelectedInCurrentMonth) {
              // Selected date is NOT in current month → navigate to selected date's month first
              needsNavigation = true;
              if (selectedBSKey) {
                const bsParts = selectedBSKey.split("-");
                if (bsParts.length === 3) {
                  targetBSYear = parseInt(bsParts[0], 10);
                  targetBSMonth = parseInt(bsParts[1], 10);
                  setCurrentBSYear(targetBSYear);
                  setCurrentBSMonth(targetBSMonth);
                  // Fetch data for selected date's month
                  if (!getDataForKey(targetBSYear, targetBSMonth)) {
                    await fetchMonthData(targetBSYear, targetBSMonth);
                  }
                }
              }
            } else if (selectedBSKey) {
              // Selected date IS in current month → use selected date's BS month
              const bsParts = selectedBSKey.split("-");
              if (bsParts.length === 3) {
                targetBSYear = parseInt(bsParts[0], 10);
                targetBSMonth = parseInt(bsParts[1], 10);
              }
            }
          }
        }
        
        // Priority 2: Value prop exists AND is in current AD month → use value prop's BS month
        if (!targetBSYear && value) {
          const dateParts = value.split("-");
          if (dateParts.length === 3) {
            const valueADYear = parseInt(dateParts[0], 10);
            const valueADMonth = parseInt(dateParts[1], 10);
            const isValueInCurrentMonth = valueADYear === currentAdYear && valueADMonth === currentAdMonth;
            if (isValueInCurrentMonth) {
              // Value is in current AD month → use value prop's BS month
              const valueBSDate = adToBS(value);
              if (valueBSDate) {
                targetBSYear = valueBSDate.bs_y;
                targetBSMonth = valueBSDate.bs_m;
              }
            }
            // If value is NOT in current month, ignore it (user has navigated)
          }
        }
        
        // Priority 3: Today's date exists AND is in current AD month → use today's BS month
        if (!targetBSYear && todayDates.adDate) {
          const dateParts = todayDates.adDate.split("-");
          if (dateParts.length === 3) {
            const todayADYear = parseInt(dateParts[0], 10);
            const todayADMonth = parseInt(dateParts[1], 10);
            const isTodayInMonth = todayADYear === currentAdYear && todayADMonth === currentAdMonth;
            if (isTodayInMonth && todayDates.bsDate) {
              // Today is in current AD month → use today's BS month
              const bsParts = todayDates.bsDate.split("-");
              if (bsParts.length === 3) {
                targetBSYear = parseInt(bsParts[0], 10);
                targetBSMonth = parseInt(bsParts[1], 10);
              }
            }
          }
        }
        
        // Priority 4: Check if current AD month is contained in current BS month data
        // This ensures we restore the BS month we were viewing before toggling to AD
        if (!targetBSYear) {
          const currentBSData = getDataForKey(currentBSYear, currentBSMonth);
          if (currentBSData && currentBSData.days) {
            // Check if the AD month is contained in the current BS month
            const hasADMonthInCurrentBS = currentBSData.days.some(day => 
              day.ad && day.ad.ad_y === currentAdYear && day.ad.ad_m === currentAdMonth
            );
            if (hasADMonthInCurrentBS) {
              // AD month is in current BS month → restore current BS month
              targetBSYear = currentBSYear;
              targetBSMonth = currentBSMonth;
            }
          }
        }
        
        // Priority 5: No selected date, no value in current month, no today, AD not in current BS → find BS month where AD month starts
        if (!targetBSYear) {
          const startBS = getADMonthStartBSMonth(currentAdYear, currentAdMonth);
          if (startBS) {
            targetBSYear = startBS.bsYear;
            targetBSMonth = startBS.bsMonth;
          }
        }
      }
      
      // Set the target BS month
      if (targetBSYear && targetBSMonth) {
        setCurrentBSYear(targetBSYear);
        setCurrentBSMonth(targetBSMonth);
        if (!getDataForKey(targetBSYear, targetBSMonth)) {
          await fetchMonthData(targetBSYear, targetBSMonth);
        }
      }
      
      // Update selected date keys: if selectedADKey exists, ensure selectedBSKey is set
      if (selectedADKey && !selectedBSKey) {
        const dateParts = selectedADKey.split("-");
        if (dateParts.length === 3) {
          const adYear = parseInt(dateParts[0], 10);
          const adMonth = parseInt(dateParts[1], 10);
          const adDay = parseInt(dateParts[2], 10);
          if (!isNaN(adYear) && !isNaN(adMonth) && !isNaN(adDay)) {
            const bsDate = adToBS(adYear, adMonth, adDay);
            if (bsDate) {
              const bsDateString = `${bsDate.bs_y}-${String(bsDate.bs_m).padStart(2, "0")}-${String(bsDate.bs_d).padStart(2, "0")}`;
              setSelectedBSKey(bsDateString);
            }
          }
        }
      }
    }

    setCalendarMode(mode);
    onCalendarModeChange?.(mode);
    
    // Update selected date keys if needed
    if (mode === "ad" && selectedBSKey && !selectedADKey) {
      const dateParts = selectedBSKey.split("-");
      if (dateParts.length === 3) {
        const bsYear = parseInt(dateParts[0], 10);
        const bsMonth = parseInt(dateParts[1], 10);
        const bsDay = parseInt(dateParts[2], 10);
        if (!isNaN(bsYear) && !isNaN(bsMonth) && !isNaN(bsDay)) {
          const adDate = bsToAD(bsYear, bsMonth, bsDay);
          if (adDate) {
            const adDateString = `${adDate.ad_y}-${String(adDate.ad_m).padStart(2, "0")}-${String(adDate.ad_d).padStart(2, "0")}`;
            setSelectedADKey(adDateString);
          }
        }
      }
    }
  };

  const handleMonthSelect = async (month: number) => {
    console.log("🔵 handleMonthSelect - calendarMode:", calendarMode, "month:", month);
    console.log("🔵 handleMonthSelect - currentBSYear:", currentBSYear, "currentBSMonth:", currentBSMonth);
    console.log("🔵 handleMonthSelect - primaryADMonth:", primaryADMonth, "primaryADYear:", primaryADYear);
    setIsMonthLoading(true);
    try {
      if (calendarMode === "bs") {
        // BS mode: treat selection as BS month
        setCurrentBSMonth(month);
        setShowMonthDropdown(false);
        // Fetch data if missing
        if (!getDataForKey(currentBSYear, month)) {
          await fetchMonthData(currentBSYear, month);
        }
        console.log("🔵 handleMonthSelect (BS) - After setCurrentBSMonth, primaryADMonth:", primaryADMonth, "primaryADYear:", primaryADYear);
        onMonthChange?.(currentBSYear, month);
      } else {
        // AD mode: treat selection as AD month
        // Use current AD year (from primaryADYear or current display)
        const displayInfo = getDisplayMonthYear();
        const adYear = primaryADYear ?? displayInfo.yearNum;
        const adMonth = month; // This is the AD month selected

        // Convert AD month to BS using day 15 as anchor
        const bsDate = adToBS(adYear, adMonth, 15);
        if (bsDate) {
          setCurrentBSYear(bsDate.bs_y);
          setCurrentBSMonth(bsDate.bs_m);
          setShowMonthDropdown(false);

          // Set primary AD month/year for AD mode display
          setPrimaryADMonth(adMonth);
          setPrimaryADYear(adYear);
          console.log("🔵 handleMonthSelect (AD) - Set primaryADMonth:", adMonth, "primaryADYear:", adYear);

          // Fetch BS month data if missing
          if (!getDataForKey(bsDate.bs_y, bsDate.bs_m)) {
            await fetchMonthData(bsDate.bs_y, bsDate.bs_m);
          }

          // Fetch all BS months for this AD month (for adModeDays)
          if (fetchAllBSMonthsForADMonth) {
            fetchAllBSMonthsForADMonth(adYear, adMonth).catch((err) => {
              console.error("Error fetching AD month days:", err);
            });
          }

          onMonthChange?.(adYear, adMonth);
        } else {
          setShowMonthDropdown(false);
          console.error(`Failed to convert AD date ${adYear}-${adMonth}-15 to BS`);
        }
      }
    } finally {
      setIsMonthLoading(false);
    }
  };

  const handleYearSelect = async (year: number) => {
    console.log("🟢 handleYearSelect - calendarMode:", calendarMode, "year:", year);
    console.log("🟢 handleYearSelect - currentBSYear:", currentBSYear, "currentBSMonth:", currentBSMonth);
    console.log("🟢 handleYearSelect - primaryADMonth:", primaryADMonth, "primaryADYear:", primaryADYear);
    setIsYearLoading(true);
    try {
      if (calendarMode === "bs") {
        // BS mode: treat selection as BS year
        setCurrentBSYear(year);
        setShowYearDropdown(false);
        // Fetch data if missing
        if (!getDataForKey(year, currentBSMonth)) {
          await fetchMonthData(year, currentBSMonth);
        }
        console.log("🟢 handleYearSelect (BS) - After setCurrentBSYear, primaryADMonth:", primaryADMonth, "primaryADYear:", primaryADYear);
        onMonthChange?.(year, currentBSMonth);
      } else {
        // AD mode: treat selection as AD year
        // Use current AD month (from primaryADMonth or current display)
        const displayInfo = getDisplayMonthYear();
        const adYear = year; // This is the AD year selected
        const adMonth = primaryADMonth ?? displayInfo.monthNum;

        // Convert AD month/year to BS using day 15 as anchor
        const bsDate = adToBS(adYear, adMonth, 15);
        if (bsDate) {
          setCurrentBSYear(bsDate.bs_y);
          setCurrentBSMonth(bsDate.bs_m);
          setShowYearDropdown(false);

          // Set primary AD month/year for AD mode display
          setPrimaryADMonth(adMonth);
          setPrimaryADYear(adYear);
          console.log("🟢 handleYearSelect (AD) - Set primaryADMonth:", adMonth, "primaryADYear:", adYear);

          // Fetch BS month data if missing
          if (!getDataForKey(bsDate.bs_y, bsDate.bs_m)) {
            await fetchMonthData(bsDate.bs_y, bsDate.bs_m);
          }

          // Fetch all BS months for this AD month (for adModeDays)
          if (fetchAllBSMonthsForADMonth) {
            fetchAllBSMonthsForADMonth(adYear, adMonth).catch((err) => {
              console.error("Error fetching AD month days:", err);
            });
          }

          onMonthChange?.(adYear, adMonth);
        } else {
          setShowYearDropdown(false);
          console.error(`Failed to convert AD date ${adYear}-${adMonth}-15 to BS`);
        }
      }
    } finally {
      setIsYearLoading(false);
    }
  };

  return {
    // State
    currentBSYear,
    currentBSMonth,
    calendarData,
    isLoading,
    isYearLoading,
    isMonthLoading,
    calendarMode,
    isIndexedDBInitialized,
    showMonthDropdown,
    showYearDropdown,
    selectedDate,
    selectedBSKey,
    selectedADKey,

    // AD mode navigation state
    primaryADMonth,
    primaryADYear,

    // Refs
    titleRef,

    // Setters
    setShowMonthDropdown,
    setShowYearDropdown,
    setSelectedDate,
    setSelectedBSKey,
    setSelectedADKey,

    // Helpers
    getCurrentKey,
    getDataForKey,
    getADMonthRange,
    getDisplayMonthYear,
    getDropdownDisplayValues,

    // Handlers
    handleDayClick,
    handlePrevMonth,
    handleNextMonth,
    handleCalendarModeChange,
    handleMonthSelect,
    handleYearSelect,
    fetchMonthData,
    fetchYearData,
    fetchAllBSMonthsForADMonth,
  };
};

// Constants that need to be available
const months = [
  "बैशाख",
  "जेठ",
  "असार",
  "श्रावण",
  "भदौ",
  "आश्विन",
  "कार्तिक",
  "मंसिर",
  "पुष",
  "माघ",
  "फागुन",
  "चैत्र",
];

const monthsEn = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const toNepaliNumber = (num: number): string => {
  const nepaliDigits = ["०", "१", "२", "३", "४", "५", "६", "७", "८", "९"];
  return num
    .toString()
    .replace(/[0-9]/g, (match) => nepaliDigits[parseInt(match)]);
};

export { months, monthsEn, toNepaliNumber };

